import {
  ImportModuleLookupCheck,
  ImportModuleLookupCode,
  ImportModuleLookupKeyLabels,
} from '../../../core/definitions/import-module';
import {blankValueFold} from '../blank-values-step/blank-values.model';
import {codeFold, foldedAliases, LookupKeyOptionControls} from '../lookup-controls';

/** A source code of a lookup as its code table shows it. */
export interface LookupCodeRow extends ImportModuleLookupCode {
  // The lookup found no table row for the code.
  missed: boolean;
  // A missed code's alias: the key the lookup looked for instead of the
  // code itself. `null` without one.
  alias: string | null;
  // The table's own row for a missed code whose alias keeps the lookup
  // from finding it. `null` when there is no alias or no such row.
  hiddenKey: ImportModuleLookupKeyLabels | null;
  // For a missed code of a case-sensitive lookup: the codes with a hit
  // that are one value with it to the blank-value reader (see
  // `blankValueFold`), so reading the code as blank would blank those
  // rows too.
  blankAlsoHits: string[];
}

/** Every code `check` lists, most rows first, with what a missed one
 * tried. */
export function lookupCodeRows(controls: LookupKeyOptionControls, check: ImportModuleLookupCheck): LookupCodeRow[] {
  const hitsByBlankValue = new Map<string, string[]>();
  // The case option applies only while the key options are on.
  if (!(controls.lookupKeyOptionsControl.value && controls.lookupCaseInsensitiveControl.value)) {
    for (const code of check.codes.filter(c => c.how !== null)) {
      const folded = blankValueFold(code.code);
      hitsByBlankValue.set(folded, [...(hitsByBlankValue.get(folded) ?? []), code.code]);
    }
  }
  const fold = codeFold(controls);
  const aliases = foldedAliases(controls);
  const tableKeys = aliases.size ? tableKeysByCode(check, fold) : null;
  return check.codes.map(code => {
    const missed = code.how === null;
    const alias = missed ? aliases.get(fold(code.code)) ?? null : null;
    return {
      ...code, missed, alias,
      hiddenKey: alias === null ? null : tableKeys?.get(fold(code.code)) ?? null,
      blankAlsoHits: missed ? hitsByBlankValue.get(blankValueFold(code.code)) ?? [] : [],
    };
  });
}

/** The table's rows `check` lists, by their key as `fold` folds it; where
 * several fold to one key the first in table order is kept. */
function tableKeysByCode(
  check: ImportModuleLookupCheck, fold: (code: string) => string,
): Map<string, ImportModuleLookupKeyLabels> {
  const keys = new Map<string, ImportModuleLookupKeyLabels>();
  for (const entry of check.key_labels) {
    const key = fold(entry.key);
    if (!keys.has(key)) {
      keys.set(key, entry);
    }
  }
  return keys;
}

/** Whether `check` lists every code the rows hold. Past its cap it
 * lists only the most frequent ones, whose counts then add up to fewer
 * rows than hold a code. */
export function listsEveryCode(check: ImportModuleLookupCheck): boolean {
  return check.codes.reduce((rows, code) => rows + code.count, 0) >= check.rows_with_code;
}

/** The label columns `check` answers for, in the order they were sent. */
export function checkLabelColumns(check: ImportModuleLookupCheck): string[] {
  const first = check.codes[0] ?? check.key_labels[0];
  return first ? Object.keys(first.labels) : [];
}

/** What a table row reads as: its first label, else its key. */
export function keyLabelText(entry: ImportModuleLookupKeyLabels): string {
  return Object.values(entry.labels).find(label => !!label) ?? entry.key;
}

/** The table keys whose key or a label holds `text`, in table order, at
 * most `limit`. */
export function matchingTableKeys(
  check: ImportModuleLookupCheck, text: string, limit: number,
): ImportModuleLookupKeyLabels[] {
  const needle = text.trim().toLocaleLowerCase();
  const matches = !needle ? check.key_labels : check.key_labels.filter(entry =>
    [entry.key, ...Object.values(entry.labels)].some(value => value?.toLocaleLowerCase().includes(needle)));
  return matches.slice(0, limit);
}
